<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import FilterPanel from '@/browse/FilterPanel.vue'
import FilterSection from '@/browse/FilterSection.vue'
import SelectFacet from '@/browse/SelectFacet.vue'
import GradeFacet from '@/browse/GradeFacet.vue'
import RadioFacet from '@/browse/RadioFacet.vue'
import SearchInput from '@/components/SearchInput.vue'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/tooltip'
import { facetOptions } from '@/browse/browse_state'
import type { BrowseState } from '@/browse/browse_state'
import type { FacetCounts } from '@/browse/browse_search'
import { GRADES, gradeSources } from '@/browse/grades'
import {
  ALGOLIA_ATTRIBUTE_BY_FILTER_KEY,
  ASSESSMENT_TYPES,
  DEFAULT_LANGUAGE,
  LANGUAGES,
  SPANISH,
  assessmentsIndexFor,
  isBaselineSelected,
  selectedLanguageOf,
  selectedSubjectOf
} from '@/browse/assessments/assessment_browse'
import { assessmentCategoriesFor, assessmentSubjectsForProfile } from '@/browse/assessments/assessment_taxonomy'
import { useUserStore } from '@/authentication/user.store'
import { useAgenticSearchStore } from '@/search/agentic_search.store'
import { sendAlgoliaAppliedAssessmentFilterEvent } from '@/search/algolia_search'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { EVENT_NAMES } from '@/tracking/analytics/event_names'

const KEYWORD_DEBOUNCE_MS = 500
const MAX_EVENT_KEYWORD_LENGTH = 120
const SELECT_A_SUBJECT = 'Select a subject'
const WIZARD_RESULTS_REPLACED_NOTE = 'Selecting a subject will replace these search results.'
// Vue 2 Browse Assessments dims unavailable subjects and languages darker than Browse Goals does.
const UNAVAILABLE_OPTION_TEXT_CLASS = 'data-[disabled]:text-gray-700'

type ToggledFilterKey = 'grades' | 'category' | 'type'

interface ToggleEvents {
  selectedEvent: string
  selectedPayloadKey: string
  unselectedEvent: string
  unselectedPayloadKey: string
}

const TOGGLE_EVENTS: Record<ToggledFilterKey, ToggleEvents> = {
  grades: {
    selectedEvent: EVENT_NAMES.BROWSE_ASSESSMENTS.SELECTED_GRADE,
    selectedPayloadKey: 'browse_assessments_selected_grade',
    unselectedEvent: EVENT_NAMES.BROWSE_ASSESSMENTS.UNSELECTED_GRADE,
    unselectedPayloadKey: 'browse_assessments_unselected_grade'
  },
  category: {
    selectedEvent: EVENT_NAMES.BROWSE_ASSESSMENTS.SELECTED_CATEGORY,
    selectedPayloadKey: 'browse_assessments_selected_category',
    unselectedEvent: EVENT_NAMES.BROWSE_ASSESSMENTS.UNSELECTED_CATEGORY,
    unselectedPayloadKey: 'browse_assessments_unselected_category'
  },
  type: {
    selectedEvent: EVENT_NAMES.BROWSE_ASSESSMENTS.SELECTED_TYPE,
    selectedPayloadKey: 'browse_assessments_selected_type',
    unselectedEvent: EVENT_NAMES.BROWSE_ASSESSMENTS.UNSELECTED_TYPE,
    unselectedPayloadKey: 'browse_assessments_unselected_type'
  }
}

const props = defineProps<{
  state: BrowseState
  facetCounts: FacetCounts
  isSearching: boolean
}>()

const emit = defineEmits<{
  refine: [key: string, values: string[]]
  toggle: [key: string, value: string]
  keyword: [keyword: string]
}>()

const analytics = getAnalyticsTracker()
const { isPremium, userId } = storeToRefs(useUserStore())
const { hasAssessmentResults: hasWizardResults } = storeToRefs(useAgenticSearchStore())

const isPremiumUser = computed(() => Boolean(isPremium.value))
const refinements = computed(() => props.state.refinements)
const selectedSubject = computed(() => selectedSubjectOf(props.state))
const selectedLanguage = computed(() => selectedLanguageOf(props.state))

const subjectOptions = computed(() => facetOptions(assessmentSubjectsForProfile(), props.facetCounts.subject, refinements.value.subject))

// Lists the grades the subject has, falling back to those in the results when the subject's
// grades are not known.
function gradesToList(facetCounts: FacetCounts): string[] {
  const subjectGrades = Object.keys(facetCounts.subjectGrades ?? {})
  if (subjectGrades.length) {
    return subjectGrades
  }
  return Object.keys(facetCounts.grades ?? {})
}

const listedGrades = computed(() => GRADES.filter((grade) => gradesToList(props.facetCounts).includes(grade)))

const gradeOptions = computed(() => facetOptions(
  gradeSources(listedGrades.value).map((source) => ({ ...source, isLocked: !isPremiumUser.value })),
  props.facetCounts.grades,
  refinements.value.grades
))

const categoryOptions = computed(() => facetOptions(
  assessmentCategoriesFor(selectedSubject.value).map((category) => ({ value: category, isLocked: !isPremiumUser.value })),
  props.facetCounts.category,
  refinements.value.category
))

const typeOptions = computed(() => facetOptions(ASSESSMENT_TYPES, props.facetCounts.type, refinements.value.type))

// There are no Spanish Baseline assessments.
function isLanguageAvailable(language: string): boolean {
  if (language !== SPANISH) {
    return true
  }
  return !isBaselineSelected(props.state)
}

const languageOptions = computed(() => facetOptions(
  LANGUAGES.map((language) => ({ value: language, isEnabled: isLanguageAvailable(language) })),
  undefined,
  refinements.value.language
))

const unavailableWithoutSubjectText = computed(() => {
  if (!selectedSubject.value) {
    return SELECT_A_SUBJECT
  }
  return undefined
})

const categoriesUnavailableText = computed(() => {
  if (!selectedSubject.value) {
    return SELECT_A_SUBJECT
  }
  if (!categoryOptions.value.length) {
    return 'No applicable categories'
  }
  return undefined
})

// Insights relevance reporting counts each filter chosen, though not one unchosen.
function reportAppliedFilter(key: string, value: string) {
  sendAlgoliaAppliedAssessmentFilterEvent({
    userToken: userId.value,
    index: assessmentsIndexFor(props.state),
    filter: `${ALGOLIA_ATTRIBUTE_BY_FILTER_KEY[key]}:${value}`
  })
}

// Choosing the selected subject again clears it.
function selectSubject(subject: string) {
  const isDeselecting = subject === selectedSubject.value
  if (isDeselecting) {
    analytics.sendEvent(EVENT_NAMES.BROWSE_ASSESSMENTS.REMOVED_SUBJECT, { browse_assessments_removed_subject: subject, ...NO_SELECTED_TYPE })
    emit('refine', 'subject', [])
    return
  }
  analytics.sendEvent(EVENT_NAMES.BROWSE_ASSESSMENTS.SELECTED_SUBJECT, { browse_assessments_selected_subject: subject })
  reportAppliedFilter('subject', subject)
  emit('refine', 'subject', [subject])
}

// Unchoosing the type, or the subject a type needs, leaves no type selected. These events are sent
// before the page applies the change, so they say so themselves rather than carry the page's type.
const NO_SELECTED_TYPE = { browse_assessments_selected_type: undefined }

function typeClearedBy(key: ToggledFilterKey) {
  if (key !== 'type') {
    return {}
  }
  return NO_SELECTED_TYPE
}

function toggleFilter(key: ToggledFilterKey, value: string) {
  const toggleEvents = TOGGLE_EVENTS[key]
  const isValueSelected = refinements.value[key].includes(value)
  if (isValueSelected) {
    analytics.sendEvent(toggleEvents.unselectedEvent, { [toggleEvents.unselectedPayloadKey]: value, ...typeClearedBy(key) })
  } else {
    analytics.sendEvent(toggleEvents.selectedEvent, { [toggleEvents.selectedPayloadKey]: value })
    reportAppliedFilter(key, value)
  }
  emit('toggle', key, value)
}

// Choosing the selected language again goes back to English.
function languageAfterChoosing(language: string): string {
  const isDeselecting = language === selectedLanguage.value
  if (isDeselecting) {
    return DEFAULT_LANGUAGE
  }
  return language
}

function selectLanguage(language: string) {
  const nextLanguage = languageAfterChoosing(language)
  const isLanguageChanging = nextLanguage !== selectedLanguage.value
  if (isLanguageChanging) {
    analytics.sendEvent(EVENT_NAMES.BROWSE_ASSESSMENTS.SELECTED_LANGUAGE, { browse_assessments_selected_language: nextLanguage })
  }
  const isChoosingAnotherLanguage = language !== selectedLanguage.value
  if (isChoosingAnotherLanguage) {
    reportAppliedFilter('language', language)
  }
  emit('refine', 'language', [nextLanguage])
}

// Every keystroke is recorded, though the search runs only once typing pauses.
function recordKeywordTyping(keyword: string) {
  if (keyword) {
    analytics.sendEvent(EVENT_NAMES.BROWSE_ASSESSMENTS.SEARCHED_KEYWORD, { browse_assessments_searched_keyword: keyword.slice(0, MAX_EVENT_KEYWORD_LENGTH) })
    return
  }
  analytics.sendEvent(EVENT_NAMES.BROWSE_ASSESSMENTS.CLEARED_KEYWORD)
}

function searchKeyword(keyword: string) {
  emit('keyword', keyword)
}
</script>

<template>
  <FilterPanel>
    <FilterSection heading="Subjects">
      <template #default="{ labelledBy }">
        <SelectFacet :options="subjectOptions" :labelled-by="labelledBy" :empty-text="SELECT_A_SUBJECT" :clearable="false" has-trigger-states :unavailable-option-text-class="UNAVAILABLE_OPTION_TEXT_CLASS" @select="selectSubject" />
      </template>
      <template v-if="hasWizardResults" #heading-info>
        <TooltipProvider :delay-duration="200">
          <Tooltip>
            <TooltipTrigger as-child>
              <button type="button" class="material-icons-outlined text-[20px] text-gray-700 hover:text-gray-900" :aria-label="WIZARD_RESULTS_REPLACED_NOTE">
                info
              </button>
            </TooltipTrigger>
            <TooltipContent side="top">{{ WIZARD_RESULTS_REPLACED_NOTE }}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </template>
    </FilterSection>
    <FilterSection v-slot="{ labelledBy }" heading="Grades" :unavailable-text="unavailableWithoutSubjectText">
      <GradeFacet :options="gradeOptions" :labelled-by="labelledBy" @toggle="toggleFilter('grades', $event)" />
    </FilterSection>
    <FilterSection v-slot="{ labelledBy }" heading="Categories" :unavailable-text="categoriesUnavailableText">
      <RadioFacet :options="categoryOptions" :labelled-by="labelledBy" @toggle="toggleFilter('category', $event)" />
    </FilterSection>
    <FilterSection v-slot="{ labelledBy }" heading="Assessment Type" :unavailable-text="unavailableWithoutSubjectText">
      <RadioFacet :options="typeOptions" :labelled-by="labelledBy" @toggle="toggleFilter('type', $event)" />
    </FilterSection>
    <FilterSection v-slot="{ labelledBy }" heading="Language" :unavailable-text="unavailableWithoutSubjectText">
      <SelectFacet :options="languageOptions" :labelled-by="labelledBy" :empty-text="DEFAULT_LANGUAGE" :clearable="false" has-trigger-states :unavailable-option-text-class="UNAVAILABLE_OPTION_TEXT_CLASS" @select="selectLanguage" />
    </FilterSection>
    <FilterSection heading="Search">
      <SearchInput
        :query="state.keyword"
        :is-searching="isSearching"
        :is-disabled="!isPremiumUser"
        label-text="Keyword search"
        hide-label
        hide-button
        placeholder-text="Keyword Search"
        :debounce-duration-ms="KEYWORD_DEBOUNCE_MS"
        input-id="browse-assessments-search-input"
        @query:typed="recordKeywordTyping"
        @query:changed="searchKeyword"
      />
    </FilterSection>
  </FilterPanel>
</template>
